import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import cx from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import ReactSelect, {
  CSSObjectWithLabel,
  SingleValue,
  StylesConfig,
} from 'react-select'
import { useDebouncedCallback } from 'use-debounce'
import { z } from 'zod'

import { Button } from '@/components/button'
import ErrorModal from '@/components/error-modal'
import { Switch } from '@/components/switch'
import Tooltip from '@/components/tooltip'
import { useCampaignClient, useIncentiveClient, useUserClient } from '@/lib/api'
import { CACHE_KEYS, invalidateIncentiveQueries } from '@/lib/cache-utils'
import { UpdateIncentiveItemApiSchema } from '@/schema/incentive.schema'
import { displayS3File } from '@/utils/s3'
import { Plus } from '@phosphor-icons/react'

type UpdatePayload = z.infer<typeof UpdateIncentiveItemApiSchema>
type SelectOption = { value: string; label: string }

// Define types locally (previously from GraphQL)
type Customer = {
  id: string
  name: string
}

type IncentiveCategory = {
  id: string
  name: string
}

type UpdateIncentiveItemInput = {
  id?: string
  name: string
  description?: string
  price: number
  type: string
  categoryId: string
  customerId?: string
  skuCode?: string
  digitalCode?: string
  picture?: string
}

export default function IncentivesStoreList() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [incentive, setIncentive] = useState<UpdateIncentiveItemInput>()
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState<{
    title?: string
    text?: string
  }>()

  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null,
  )
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  )
  const incentivesPerPage = 10
  const [localSearch, setLocalSearch] = useState('')
  const incentiveClient = useIncentiveClient()
  const campaignClient = useCampaignClient()
  const userClient = useUserClient()

  // Fetch incentives store with React Query
  const {
    data: incentivesResponse,
    isLoading: loadingIncentivesStore,
    error: errorIncentivesStore,
    refetch: refetchIncentivesStore,
  } = useQuery({
    queryKey: [
      CACHE_KEYS.INCENTIVES_STORE,
      currentPage,
      incentivesPerPage,
      searchQuery,
      selectedCustomerId,
      selectedCategoryId,
    ],
    queryFn: async () =>
      await incentiveClient.getStore.query({
        query: {
          page: currentPage.toString(),
          pageSize: incentivesPerPage.toString(),
          search: searchQuery || undefined,
          customerId: selectedCustomerId || undefined,
          categoryId: selectedCategoryId || undefined,
        },
      }),
  })

  const incentives =
    incentivesResponse?.status === 200
      ? { incentivesStore: incentivesResponse.body }
      : null

  // Fetch customers with React Query
  const { data: customersResponse, isLoading: customersLoading } = useQuery({
    queryKey: [CACHE_KEYS.CUSTOMERS],
    queryFn: async () => {
      const response = await campaignClient.customers.query()
      return response.status === 200 ? response.body : []
    },
  })

  const customersData = useMemo(
    () => ({
      customers: (customersResponse ?? []) as Customer[],
    }),
    [customersResponse],
  )

  // Fetch categories with React Query
  const { data: categoriesResponse, isLoading: categoriesLoading } = useQuery({
    queryKey: [CACHE_KEYS.INCENTIVE_CATEGORIES],
    queryFn: async () => await incentiveClient.getCategories.query(),
  })

  const categoriesData = useMemo(
    () => ({
      incentiveCategories: (categoriesResponse?.status === 200
        ? categoriesResponse.body
        : []) as IncentiveCategory[],
    }),
    [categoriesResponse],
  )

  const incentivesList = incentives?.incentivesStore.incentives || []
  const totalIncentives = incentives?.incentivesStore?.pagination.total || 0
  const totalPages = Math.ceil(totalIncentives / incentivesPerPage)
  const startIndex = (currentPage - 1) * incentivesPerPage + 1
  const endIndex = Math.min(currentPage * incentivesPerPage, totalIncentives)

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (incentivesList.length === incentivesPerPage) {
      setCurrentPage(currentPage + 1)
    }
  }

  const debouncedSearch = useDebouncedCallback((text: string) => {
    setSearchQuery(text)
    setCurrentPage(1)
  }, 1000)

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalSearch(e.target.value)
      debouncedSearch(e.target.value)
    },
    [debouncedSearch],
  )

  const handleCustomerChange = useCallback(
    (newValue: SingleValue<{ value: string; label: string }>) => {
      setSelectedCustomerId(newValue ? newValue.value : null)
      setCurrentPage(1)
    },
    [],
  )

  const handleCategoryChange = useCallback(
    (newValue: SingleValue<{ value: string; label: string }>) => {
      setSelectedCategoryId(newValue ? newValue.value : null)
      setCurrentPage(1)
    },
    [],
  )

  const customerOptions = customersData?.customers
    ? [
        { value: '', label: t('incentives_store.all_customers') },
        ...customersData.customers.map((customer: Customer) => ({
          value: customer.id,
          label: customer.name,
        })),
      ]
    : []

  const categoryOptions = categoriesData?.incentiveCategories
    ? [
        { value: '', label: t('incentives_store.all_categories') },
        ...categoriesData.incentiveCategories.map(
          (category: IncentiveCategory) => ({
            value: category.id,
            label: t(`incentive_categories.${category.name}`),
          }),
        ),
      ]
    : []

  const { data: userResponse, isLoading: loadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => await userClient.getMe.query(),
  })

  const user = useMemo(
    () => ({
      user: userResponse?.status === 200 ? userResponse.body : null,
    }),
    [userResponse],
  )

  const updateMutation = useMutation({
    mutationFn: (incentiveItem: UpdateIncentiveItemInput) =>
      incentiveClient.update.mutate({
        params: { id: incentiveItem.id as string },
        body: incentiveItem as UpdatePayload,
      }),
    onSuccess: (_, variables) => {
      setShowErrorModal(false)
      invalidateIncentiveQueries(queryClient, {
        customerId: variables.customerId,
        categoryId: variables.categoryId,
        incentiveId: variables.id,
      })
    },
  })

  const rowContentClassName = 'px-5 py-4 text-sm text-gray-100 items-center '
  const tableHeadersClassName =
    'whitespace-nowrap px-5 py-4 text-left text-sm font-normal text-gray-100'

  const sharedSelectStyles: StylesConfig<SelectOption, false> = {
    indicatorSeparator: () => ({ display: 'none' }),
    control: (provided) =>
      ({
        ...provided,
        width: '165px',
        height: '40px',
      }) as CSSObjectWithLabel,
  }

  if (
    loadingIncentivesStore ||
    loadingUser ||
    customersLoading ||
    categoriesLoading
  )
    return <div>{t('loading')}</div>
  if (errorIncentivesStore) return <div>{t('error')}</div>

  return (
    <>
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:justify-between">
        <div className="flex flex-col md:flex-row gap-2 items-center">
          <input
            type="text"
            placeholder={t('incentives_store.search_placeholder')}
            value={localSearch}
            onChange={handleSearchChange}
            className="border border-border rounded-md p-2 h-10 bg-white"
          />
          <ReactSelect<SelectOption>
            options={customerOptions}
            placeholder="Customer"
            isSearchable={false}
            value={customerOptions.find(
              (option) => option.value === selectedCustomerId,
            )}
            onChange={handleCustomerChange}
            styles={sharedSelectStyles}
            defaultValue={customerOptions[0]}
          ></ReactSelect>
          <ReactSelect<SelectOption>
            options={categoryOptions}
            placeholder="Category"
            isSearchable={false}
            value={categoryOptions.find(
              (option) => option.value === selectedCategoryId,
            )}
            onChange={handleCategoryChange}
            styles={sharedSelectStyles}
            defaultValue={categoryOptions[0]}
          ></ReactSelect>
        </div>
        <div className="flex flex-col md:flex-row items-center">
          <Button
            label={t('incentives_store.new_incentive')}
            onClick={() => navigate('/incentives/create')}
            IconLeft={Plus}
            className="border border-primary hover:bg-primary hover:!text-white focus:bg-primary focus:!text-white focus-visible:outline focus:ring-primary"
            size="small"
          />
        </div>
      </div>
      <div
        className={cx(
          !incentives?.incentivesStore?.incentives?.length
            ? undefined
            : 'overflow-scroll',
          'sm:rounded-lg bg-white',
        )}
      >
        {incentivesList.length ? (
          <div className="sm:rounded-lg ">
            <table className="w-full table-auto">
              <thead className="">
                <tr className="border-b border-border h-14 font-bold">
                  <th scope="col" className={tableHeadersClassName}>
                    {t('incentives_store.featured')}
                  </th>
                  <th scope="col" className={tableHeadersClassName}>
                    {t('incentives_store.product_info')}
                  </th>
                  <th scope="col" className={tableHeadersClassName}>
                    {t('incentives_store.price')}
                  </th>
                  <th scope="col" className={tableHeadersClassName}>
                    {t('incentives_store.category')}
                  </th>
                  <th scope="col" className={tableHeadersClassName}>
                    {t('incentives_store.description')}
                  </th>
                  <th scope="col" className={tableHeadersClassName}>
                    {t('incentives_store.claimed')}
                  </th>
                  <th scope="col" className={tableHeadersClassName}>
                    {t('incentives_store.disabled_active')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-white">
                {incentivesList.map((incentiveWithClaimedStatus) => {
                  const { incentive } = incentiveWithClaimedStatus
                  return (
                    <tr
                      onClick={() => {
                        navigate(`/incentives/update/${incentive?.id}`)
                      }}
                      className="border-b border-border h-14 cursor-pointer"
                      key={incentive?.id}
                    >
                      <td className={cx(rowContentClassName, 'w-24')}>
                        <Tooltip
                          child={
                            <div className="flex items-center gap-x-2">
                              <div
                                className={cx(
                                  incentive?.isActive ? 'bg-primary' : 'bg-ui',
                                  'w-2 h-2 rounded-full',
                                )}
                              ></div>
                              {incentiveWithClaimedStatus.featured}
                            </div>
                          }
                          text={t('incentives_store.featured_tooltip', {
                            numberOfCampaigns:
                              incentiveWithClaimedStatus.featured,
                          })}
                        />
                      </td>
                      <td
                        className={cx(
                          rowContentClassName,
                          'flex items-center gap-2 max-w-64',
                        )}
                      >
                        <div className="w-20">
                          <img
                            className="rounded-md max-w-20 max-h-14"
                            src={
                              incentive?.picture
                                ? displayS3File(incentive.picture)
                                : ''
                            }
                          />
                        </div>
                        <div>
                          <p className="font-bold text-black-100">
                            {incentive?.name}
                          </p>
                          <p className="font-normal text-gray-200">
                            SKU: {incentive?.skuCode}
                          </p>
                        </div>
                      </td>
                      <td className={cx(rowContentClassName, 'w-40')}>
                        <span className="font-bold">
                          &#8364; {incentive?.price}
                        </span>{' '}
                        /{t('incentives_store.pcs')}
                      </td>
                      <td className={cx(rowContentClassName, 'w-40')}>
                        {incentive?.categories?.[0]?.incentiveCategory?.name
                          ? t(
                              `incentive_categories.${incentive?.categories?.[0]?.incentiveCategory?.name}`,
                            )
                          : undefined}
                      </td>
                      <td className={cx(rowContentClassName, 'min-w-96')}>
                        {incentive?.description}
                      </td>
                      <td className={cx(rowContentClassName, 'w-40')}>
                        {incentiveWithClaimedStatus?.totalClaimedIncentives}
                      </td>
                      <td
                        className={cx(
                          rowContentClassName,
                          'align-middle w-24 text-center',
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                        }}
                      >
                        <Switch
                          defaultChecked={incentive?.isActive ?? false}
                          className="data-[state=checked]:bg-green-400"
                          disabled={
                            user?.user?.role === 'Superadmin'
                              ? false
                              : !(
                                  user?.user?.customerId ===
                                  incentive?.customerId
                                )
                          }
                          onCheckedChange={(toggle) => {
                            const numberOfCampaigns =
                              incentiveWithClaimedStatus?.featured ?? 0
                            const incentiveItem = {
                              id: incentive?.id ?? '',
                              name: incentive?.name ?? '',
                              isActive: toggle,
                              price: incentive?.price ?? 0,
                              picture: incentive?.picture ?? '',
                              description: incentive?.description ?? '',
                              categoryId: incentive?.categoryId ?? '',
                              type: incentive?.type ?? '',
                              campaignId: incentive?.campaignId ?? null,
                            }
                            numberOfCampaigns > 0 && toggle == false
                              ? (setShowErrorModal(true),
                                setErrorMessage({
                                  title: t(
                                    'incentive_errors_modal.update_error_modal_title',
                                    {
                                      numberOfCampaigns,
                                    },
                                  ),
                                  text: t(
                                    `incentive_errors_modal.update_incentive_item_is_used`,
                                    {
                                      numberOfCampaigns,
                                    },
                                  ),
                                }),
                                incentiveItem && setIncentive(incentiveItem))
                              : incentiveItem &&
                                updateMutation.mutate(incentiveItem)
                          }}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="w-full text-center h-96 mt-28">
            <p>{t('incentives_store.no_incentives')}</p>
            <p>{t('incentives_store.please_add')}</p>
          </div>
        )}
        <div className="mt-4 flex justify-between p-4">
          <div className="flex items-center">
            {t('incentives_store.pagination_info', {
              currentPage,
              totalPages,
              startIndex,
              endIndex,
              totalIncentives,
            })}
          </div>
          <div className="flex gap-2">
            <Button
              label={t('incentives_store.previous')}
              className="border border-border"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              size="small"
            ></Button>
            <Button
              label={t('incentives_store.next')}
              className="border border-border h-10"
              onClick={handleNextPage}
              disabled={incentivesList.length < incentivesPerPage}
              size="small"
            ></Button>
          </div>
        </div>
      </div>
      {showErrorModal &&
        createPortal(
          <ErrorModal
            hasOnSubmit={true}
            title={errorMessage?.title ?? ''}
            error={errorMessage?.text ?? ''}
            onClick={() => {
              setErrorMessage({})
              setIncentive(undefined)

              // Re-fetch incentives store to update the UI
              refetchIncentivesStore()
              setShowErrorModal(false)
            }}
            onSubmit={() => {
              incentive && updateMutation.mutate(incentive)
            }}
            submitButtonText={t('disable')}
          />,
          document.body,
        )}
    </>
  )
}
